import { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';

/** Shared field chrome for campaign create forms */
export function FieldLabel({
  children,
  required,
  optional,
  hint,
  htmlFor,
}: {
  children: React.ReactNode;
  required?: boolean;
  optional?: boolean;
  hint?: string;
  htmlFor?: string;
}) {
  return (
    <div className="mb-1.5 flex flex-col gap-0.5 text-left">
      <label
        htmlFor={htmlFor}
        className="text-[13px] font-semibold text-[var(--dash-ink)]"
      >
        {children}
        {required ? (
          <span className="ml-1 text-red-500" aria-hidden>
            *
          </span>
        ) : null}
        {optional && !required ? (
          <span className="ml-1 text-[11px] font-medium text-[var(--dash-muted)]">
            optional
          </span>
        ) : null}
      </label>
      {hint ? (
        <p className="m-0 text-[11px] leading-snug text-[var(--dash-muted)]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="m-0 mt-1 text-xs font-medium text-red-500" role="alert">
      {message}
    </p>
  );
}

export function SectionCard({
  step,
  title,
  description,
  children,
  variant = 'card',
}: {
  step?: number | string;
  title: string;
  description?: string;
  children: React.ReactNode;
  /** `plain` = no bordered shell (for nested modals / dense layouts). */
  variant?: 'card' | 'plain';
}) {
  const isPlain = variant === 'plain';

  return (
    <section className={isPlain ? 'text-left' : 'camp-section'}>
      <header
        className={
          isPlain
            ? 'mb-3 text-left'
            : 'mb-3 flex items-center gap-2.5 border-b border-[var(--dash-line)] pb-2.5 text-left'
        }
      >
        {!isPlain && step != null ? (
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[var(--dash-brand-soft)] text-xs font-bold text-[var(--dash-brand)]">
            {step}
          </div>
        ) : null}
        <div className="min-w-0">
          <h2
            className={`m-0 font-semibold tracking-tight text-[var(--dash-ink)] ${
              isPlain ? 'text-sm' : 'text-[15px]'
            }`}
          >
            {title}
          </h2>
          {description ? (
            <p className="m-0 mt-0.5 text-xs text-[var(--dash-muted)]">
              {description}
            </p>
          ) : null}
        </div>
      </header>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

export const campInputClass =
  'camp-input w-full rounded-lg border bg-[var(--dash-soft)] px-3 py-2.5 text-sm text-[var(--dash-ink)] outline-none transition placeholder:text-[#9AA8B8] focus:border-[var(--dash-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--dash-brand)]/15';

/** Soft fill, no border — for dense / modal layouts. */
export const campInputPlainClass =
  'camp-input w-full appearance-none rounded-xl border-0 bg-[#EEF2F6] px-3.5 py-3 text-sm text-[var(--dash-ink)] shadow-none outline-none transition placeholder:text-[#9AA8B8] hover:bg-[#E8EDF3] focus:bg-[#E8EDF3] focus:ring-2 focus:ring-[var(--dash-brand)]/20';

export function campInputClassFor(variant: 'card' | 'plain' = 'card') {
  return variant === 'plain' ? campInputPlainClass : campInputClass;
}

type CampSelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  variant?: 'card' | 'plain';
};

/** Native select with a consistent chevron (hides the browser arrow). */
export const CampSelect = forwardRef<HTMLSelectElement, CampSelectProps>(
  function CampSelect(
    { className, variant = 'card', disabled, children, ...props },
    ref,
  ) {
    const base = campInputClassFor(variant);
    return (
      <div className="relative">
        <select
          ref={ref}
          disabled={disabled}
          className={`${base} cursor-pointer appearance-none pr-9 disabled:cursor-not-allowed disabled:opacity-60 ${className ?? ''}`}
          {...props}
        >
          {children}
        </select>
        <ChevronDown
          className={`pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-[var(--dash-muted)] ${
            disabled ? 'opacity-50' : ''
          }`}
          aria-hidden
        />
      </div>
    );
  },
);
